import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import TeamLogo from "./TeamLogo";
import { SBOddsButton } from "./sb";
import { hasStartedPregame } from "@/lib/sportsbookPriority";

/* SBBoardGame — one game as a DraftKings-style columnar row (design 02b): two
   team rows (away then home), each with a crest + rank + name + live score on the
   left and aligned Spread / Total / Moneyline price cells on the right, then a
   thin live meta strip (clock + More bets). Prices are the existing SBOddsButton
   so selection/lock/movement all behave exactly like the rest of the book. */

// Column template shared by the section header and every game row so the price
// columns line up perfectly under SPREAD / TOTAL / MONEYLINE.
export const BOARD_COLS = "minmax(0,1fr) 92px 92px 92px";
export const BOARD_COLS_SM = "minmax(0,1fr) 74px 74px 74px";

// Friendly, on-brand league names for the raw feed codes (mockup shows
// "College Football", not "NCAAF").
const LEAGUE_LABEL = {
  NCAAF: "College Football", NCAAB: "College Basketball", NFL: "NFL", NBA: "NBA",
  MLB: "MLB", NHL: "NHL", WNBA: "WNBA", MLS: "MLS", EPL: "Premier League",
  UCL: "Champions League", "LA LIGA": "La Liga", "SERIE A": "Serie A",
  BUNDESLIGA: "Bundesliga", "LIGUE 1": "Ligue 1", UFC: "UFC", ATP: "ATP", WTA: "WTA",
};
export function leagueLabel(code) {
  if (!code) return "";
  return LEAGUE_LABEL[String(code).toUpperCase()] || code;
}

// The book's abbreviation embedded in a moneyline label, e.g. "TEX Rangers".
function labelAbbr(o) {
  const m = /^([A-Z0-9]{2,4})\s/.exec((o?.label || "").trim());
  return m ? m[1] : "";
}
const signed = (line) => {
  const n = Number(line);
  if (!Number.isFinite(n)) return line;
  return n > 0 ? `+${n}` : `${n}`;
};

export function SBColumnHeader({ compact = false }) {
  return (
    <div
      className="grid items-center px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#71717a]"
      style={{ gridTemplateColumns: compact ? BOARD_COLS_SM : BOARD_COLS }}
    >
      <span className="text-[#52525b]">{/* team column */}</span>
      <span className="text-center">Spread</span>
      <span className="text-center">Total</span>
      <span className="text-center">Moneyline</span>
    </div>
  );
}

function TeamLine({ event, side, rank, name, score, onPick, isPicked, locked, compact, sgp }) {
  const outs = event.outcomes || [];
  const ml = outs.find((o) => o.market === "moneyline" && o.side === side);
  const spread = outs.find((o) => o.market === "spread" && o.side === side);
  const total = outs.find((o) => o.market === "total" && o.side === (side === "away" ? "over" : "under"));
  const size = compact ? "sm" : "md";
  const cell = (o, label) =>
    o ? (
      <SBOddsButton
        testid={`odds-${event.id}-${o.id}`}
        label={label}
        price={o.odds}
        size={size}
        selected={isPicked(event.id, o.id)}
        locked={locked || o.suspended === true || o.active === false}
        onClick={() => onPick(event, o)}
      />
    ) : (
      <div className="flex items-center justify-center rounded-xl border border-[#1F1F23] bg-[#0F0F12] text-[#3f3f46]" style={{ height: compact ? 46 : 52 }}>—</div>
    );

  return (
    <div className="grid items-center gap-2 px-3" style={{ gridTemplateColumns: compact ? BOARD_COLS_SM : BOARD_COLS }}>
      <div className="flex min-w-0 items-center gap-2.5 pr-2">
        <TeamLogo name={name} labelAbbr={labelAbbr(ml)} league={event.league} sport={event.sport} size={compact ? 26 : 30} />
        <div className="flex min-w-0 items-center gap-1.5">
          {rank ? <span className="shrink-0 text-[11px] font-bold text-[#71717a]">{rank}</span> : null}
          <span className="truncate text-[14px] font-bold text-white">{name}</span>
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {sgp && <Icon name="layers" size={13} className="text-[#71717a]" />}
          {score != null && <span className="font-display text-[18px] font-extrabold tabular-nums text-white">{score}</span>}
        </span>
      </div>
      {cell(spread, spread ? signed(spread.line) : null)}
      {cell(total, total ? `${side === "away" ? "O" : "U"} ${total.line}` : null)}
      {cell(ml, null)}
    </div>
  );
}

// Two team rows only (column header optional) — reused on the event page's
// "Game lines" section so the lines read exactly like the board.
export function SBGameLines({ event, onPick, isPicked, compact = false, header = true, scores = false }) {
  const isLive = event.status === "live";
  const locked = !(event.status === "open" || event.status === "live") || hasStartedPregame(event);
  const liveScore = isLive && event.gs_home_score != null;
  const awayScore = scores ? (liveScore ? event.gs_away_score : (event.away_score ?? null)) : null;
  const homeScore = scores ? (liveScore ? event.gs_home_score : (event.home_score ?? null)) : null;
  return (
    <div>
      {header && <SBColumnHeader compact={compact} />}
      <div className="space-y-2">
        <TeamLine event={event} side="away" name={event.away_team} rank={event.away_rank} score={awayScore}
          onPick={onPick} isPicked={isPicked} locked={locked} compact={compact} />
        <TeamLine event={event} side="home" name={event.home_team} rank={event.home_rank} score={homeScore}
          onPick={onPick} isPicked={isPicked} locked={locked} compact={compact} />
      </div>
    </div>
  );
}

// The live/clock + "More bets" strip under a game.
function MetaStrip({ event }) {
  const isLive = event.status === "live";
  return (
    <div className="flex items-center justify-between gap-2 px-3 pt-2">
      <div className="flex min-w-0 items-center gap-2 text-[12px] text-[#a1a1aa]">
        {isLive ? (
          <>
            <span className="flex items-center gap-1 rounded-md bg-[#EF4444] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
            </span>
            {event.gs_period && <span className="truncate font-semibold tabular-nums text-white/80">{event.gs_period}</span>}
          </>
        ) : (
          <span className="flex items-center gap-1 text-[#71717a]">
            <Icon name="clock" size={12} />
            {event.start_time ? new Date(event.start_time).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "Upcoming"}
          </span>
        )}
      </div>
      <Link to={`/event/${event.id}`} data-testid={`more-bets-${event.id}`}
        className="flex shrink-0 items-center gap-1 text-[12px] font-bold text-[#a1a1aa] hover:text-white">
        More bets <Icon name="chevron-right" size={14} />
      </Link>
    </div>
  );
}

// A game inside the league table. `flush` (board use) drops the card so the odds
// columns line up under the shared SPREAD/TOTAL/MONEYLINE header, separated by a
// divider; without it the game renders as a standalone card.
export default function SBBoardGame({ event, onPick, isPicked, compact = false, flush = false }) {
  const isLive = event.status === "live";
  const locked = !(event.status === "open" || event.status === "live") || hasStartedPregame(event);
  const liveScore = isLive && event.gs_home_score != null;
  const awayScore = liveScore ? event.gs_away_score : (event.away_score != null ? event.away_score : null);
  const homeScore = liveScore ? event.gs_home_score : (event.home_score != null ? event.home_score : null);

  const rows = (
    <>
      <div className="space-y-2 pt-3">
        <TeamLine event={event} side="away" name={event.away_team} rank={event.away_rank} score={awayScore}
          onPick={onPick} isPicked={isPicked} locked={locked} compact={compact} />
        <TeamLine event={event} side="home" name={event.home_team} rank={event.home_rank} score={homeScore}
          onPick={onPick} isPicked={isPicked} locked={locked} compact={compact} />
      </div>
      <MetaStrip event={event} />
    </>
  );

  if (flush) {
    return (
      <div data-testid={`event-${event.id}`} className="border-t border-[#1F1F23] pb-3 first:border-t-0">
        {rows}
      </div>
    );
  }
  return (
    <div data-testid={`event-${event.id}`}
      className={`overflow-hidden rounded-2xl border ${isLive ? "border-[#EF4444]/25" : "border-[#1F1F23]"} bg-gradient-to-b from-[#141417] to-[#101013] pb-3`}>
      {rows}
    </div>
  );
}
